
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useSearchParams } from "@/lib/router-compat";
import { useRouter } from "@/lib/router-compat";
import Link from "@/components/ui/link-compat";
import { Plus, CheckSquare, Mail, Phone, MapPin, Users, User, UserCheck, FolderKanban, Handshake, Pencil, Building2, FileText } from "lucide-react";
import { useContact, useCompanyEmployees, useDeleteContact } from "@/hooks/useContacts";
import { useContactStore, useTaskStore, useUserStore } from "@/stores/StoreProvider";
import { RelatedTable } from "@/components/ui/related-table";
import { Checkbox } from "@/components/ui/checkbox";
import { CrmTaskStatus } from "@/types/crm";
import { InlineField } from "@/components/ui/inline-field";
import { InlineSelectField } from "@/components/ui/inline-select-field";
import { usePageTitle } from '@/hooks/usePageTitle';
import { useDeferredMount } from '@/hooks/useDeferredMount';
import { useEntityTimeline } from "@/hooks/useTimeline";
import { useUpdateNote, useDeleteNote } from "@/hooks/useNotes";
import { useUploadAttachments, useDeleteAttachment } from "@/hooks/useNoteAttachments";
import { useAuth } from "@/contexts/AuthContext";
import { ActivityTimeline } from "@/components/ui/activity-timeline";
import { useContactDeals } from "@/hooks/useDeals";
import { useContactProjects } from "@/hooks/useProjects";
import { useTasks } from "@/hooks/useTasks";
import { ContactType, DealStatus, CrmTaskPriority, type CrmTask } from "@/types/crm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getStatusClasses } from "@/lib/status-variants";
import { lazy, Suspense } from "react";
import { ContactDetailHeader } from "@/components/contacts/contact-detail-header";
const NoteComposer = lazy(() => import("@/components/notes/note-composer").then(m => ({ default: m.NoteComposer })));
const DealDialog = lazy(() => import("@/components/deals/deal-dialog").then(m => ({ default: m.DealDialog })));
const TaskDialog = lazy(() => import("@/components/tasks/task-dialog").then(m => ({ default: m.TaskDialog })));
const ProjectDialog = lazy(() => import("@/components/projects/project-dialog").then(m => ({ default: m.ProjectDialog })));
const ContactDialog = lazy(() => import("@/components/contacts/contact-dialog").then(m => ({ default: m.ContactDialog })));
import { DetailPageSkeleton } from "@/components/ui/skeletons";
import { DeleteConfirmation } from "@/components/ui/delete-confirmation";

const ContactDetailPage = observer(function ContactDetailPage() {
  const bodyReady = useDeferredMount();
  const searchParams = useSearchParams();
  const id = searchParams.get("id") ?? "";
  const { data: contact, isLoading: contactLoading } = useContact(id);
  const contactStore = useContactStore();
  const taskStore = useTaskStore();
  const userStore = useUserStore();
  const saveField = (field: string) => (value: string) => {
    contactStore.update(id, { [field]: value || null });
  };
  const ownerOptions = userStore.all.map((u) => ({
    value: u.id,
    label: u.displayName || u.email,
  }));
  const companyOptions = contactStore.all
    .filter((c) => c.type === ContactType.Company)
    .map((c) => ({ value: c.id, label: c.name }));
  usePageTitle(contact?.name ?? 'Contact');
  const { userId } = useAuth();
  const timeline = useEntityTimeline("contact", id);
  const timelineEntries = timeline.entries;
  const updateNote = useUpdateNote("contact", id);
  const deleteNote = useDeleteNote("contact", id);
  const uploadAttachments = useUploadAttachments("contact", id);
  const deleteAttachment = useDeleteAttachment("contact", id);
  const { data: deals = [] } = useContactDeals(id);

  const deleteContact = useDeleteContact();
  const router = useRouter();
  const [isDealDialogOpen, setIsDealDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { data: tasks = [] } = useTasks({ contactId: id });
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const { data: projects = [] } = useContactProjects(id);
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const [isEmployeeDialogOpen, setIsEmployeeDialogOpen] = useState(false);

  const isCompany = contact?.type === ContactType.Company;
  const { data: employees = [] } = useCompanyEmployees(
    isCompany ? contact?.id ?? "" : ""
  );

  if (!bodyReady || contactLoading) {
    return <DetailPageSkeleton />;
  }

  if (!contact) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Contact not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ContactDetailHeader contact={contact} onSaveName={(name) => contactStore.update(id, { name })} onDelete={() => setIsDeleteOpen(true)} />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
        {/* Main content: notes & activity */}
        {/* Mobile-only bottom inset: the shell's scroll-container padding is not
            honored by Chromium flex scrolling, so clear the fixed bottom nav here. */}
        <div className="space-y-4 min-w-0 order-2 lg:order-1 pb-[calc(var(--mobile-bottom-nav-height)+env(safe-area-inset-bottom))] lg:pb-0">
          <NoteComposer entityType="contact" entityId={id} />
          <ActivityTimeline
            entries={timelineEntries}
            isLoading={timeline.isLoading}
            hasMore={timeline.hasMore}
            onLoadMore={() => timeline.loadMore()}
            isLoadingMore={timeline.isLoadingMore}
            currentEntityId={id}
            onEditNote={(noteId, content) => updateNote.mutateAsync({ noteId, content })}
            onDeleteNote={(noteId) => deleteNote.mutateAsync(noteId)}
            onUploadFiles={(noteId, files) => uploadAttachments.mutateAsync({ noteId, files }).then(() => {})}
            onDeleteAttachment={(noteId, attachmentId) => deleteAttachment.mutate({ noteId, attachmentId })}
            currentUserId={userId ?? undefined}
          />
          <Button className="h-11 w-full lg:hidden" onClick={() => setIsEditDialogOpen(true)}>Edit contact</Button>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4 order-1 lg:order-2 lg:sticky lg:top-20 lg:self-start">
          {/* Details card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">Details</CardTitle>
                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setIsEditDialogOpen(true)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {contact.type === ContactType.Person && (
                  <InlineSelectField
                    label="Company"
                    value={contact.companyId ?? null}
                    options={companyOptions}
                    onSave={(val) => contactStore.update(id, { companyId: val || null })}
                    icon={<Building2 className="h-4 w-4" />}
                    emptyText="No company"
                    placeholder="Select company…"
                  />
                )}
                <InlineField
                  label="Email"
                  value={contact.email}
                  onSave={saveField("email")}
                  type="email"
                  icon={<Mail className="h-4 w-4" />}
                />
                <InlineField
                  label="Phone"
                  value={contact.phone}
                  onSave={saveField("phone")}
                  type="tel"
                  icon={<Phone className="h-4 w-4" />}
                />
                <InlineField
                  label="Address"
                  value={contact.address}
                  onSave={saveField("address")}
                  multiline
                  icon={<MapPin className="h-4 w-4" />}
                />
                <InlineSelectField
                  label="Owner"
                  value={contact.ownerId ?? null}
                  options={ownerOptions}
                  onSave={(val) => contactStore.update(id, { ownerId: val || null })}
                  icon={<UserCheck className="h-4 w-4" />}
                  emptyText="No owner"
                  placeholder="Select owner…"
                />
                <InlineField
                  label="Notes"
                  value={contact.notes}
                  onSave={saveField("notes")}
                  multiline
                  icon={<FileText className="h-4 w-4" />}
                  emptyText="Add a note…"
                />
              </CardContent>
            </Card>

          <RelatedTable
            icon={<Handshake className="h-4 w-4" />}
            title="Deals"
            count={deals.length}
            rows={deals}
            getRowKey={(deal) => deal.id}
            onRowClick={(deal) => router.push(`/dashboard/deal?id=${deal.id}`)}
            quickAdd={{ ariaLabel: "Add deal", onClick: () => setIsDealDialogOpen(true) }}
            emptyText="No deals yet."
            columns={[
              {
                key: "name",
                render: (deal) => (
                  <div className="truncate">
                    <span>{deal.dealTypeName}</span>
                    {deal.description && (
                      <span className="ml-1.5 text-muted-foreground">· {deal.description}</span>
                    )}
                  </div>
                ),
              },
              {
                key: "status",
                render: (deal) => {
                  const statusLabel =
                    deal.status === DealStatus.Won ? "Won" : deal.status === DealStatus.Lost ? "Lost" : "Open";
                  return (
                    <Badge className={`text-xs ${getStatusClasses(statusLabel)}`}>
                      {statusLabel}
                    </Badge>
                  );
                },
              },
              {
                key: "owner",
                render: (deal) =>
                  deal.ownerName ? (
                    <span
                      title={deal.ownerName}
                      className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground"
                    >
                      {deal.ownerName
                        .split(/\s+/)
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((part) => part[0]?.toUpperCase())
                        .join("")}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  ),
              },
            ]}
          />

          {/* Employees card (companies only) */}
          {isCompany && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                    <Users className="h-4 w-4" />
                    Employees ({employees.length})
                  </CardTitle>
                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setIsEmployeeDialogOpen(true)}>
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {employees.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-2">No employees yet.</p>
                ) : (
                  <div className="space-y-1">
                    {employees.map((emp) => (
                      <Link
                        key={emp.id}
                        href={`/dashboard/contact?id=${emp.id}`}
                        className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-md hover:bg-accent transition-colors"
                      >
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                        {emp.name}
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <RelatedTable
            icon={<FolderKanban className="h-4 w-4" />}
            title="Projects"
            count={projects.length}
            rows={projects}
            getRowKey={(project) => project.id}
            onRowClick={(project) => router.push(`/dashboard/project?id=${project.id}`)}
            quickAdd={{ ariaLabel: "Add project", onClick: () => setIsProjectDialogOpen(true) }}
            emptyText="No projects yet."
            columns={[
              { key: "name", render: (project) => <span className="truncate">{project.name}</span> },
              {
                key: "deals",
                render: (project) => (
                  <span className="text-xs text-muted-foreground">{project.dealsCount} deals</span>
                ),
              },
            ]}
          />

          <RelatedTable
            icon={<CheckSquare className="h-4 w-4" />}
            title="Tasks"
            count={tasks.length}
            rows={tasks}
            getRowKey={(task) => task.id}
            onRowClick={(task) => router.push(`/dashboard/task?id=${task.id}`)}
            quickAdd={{ ariaLabel: "Add task", onClick: () => setIsTaskDialogOpen(true) }}
            emptyText="No tasks yet."
            columns={[
              {
                key: "done",
                render: (task: CrmTask) => (
                  <span onClick={(event) => event.stopPropagation()} className="inline-flex">
                    <Checkbox
                      checked={task.status === CrmTaskStatus.Completed}
                      aria-label={`Complete ${task.title}`}
                      onCheckedChange={() =>
                        taskStore.update(task.id, { status: CrmTaskStatus.Completed })
                      }
                    />
                  </span>
                ),
              },
              { key: "title", render: (task: CrmTask) => <span className="truncate">{task.title}</span> },
              {
                key: "priority",
                render: (task: CrmTask) => (
                  <Badge
                    variant={
                      task.priority === CrmTaskPriority.High
                        ? "destructive"
                        : task.priority === CrmTaskPriority.Medium
                          ? "default"
                          : "secondary"
                    }
                    className="text-xs"
                  >
                    {task.priority === CrmTaskPriority.High
                      ? "High"
                      : task.priority === CrmTaskPriority.Medium
                        ? "Med"
                        : "Low"}
                  </Badge>
                ),
              },
            ]}
          />
        </aside>
      </div>

      {isDealDialogOpen && <DealDialog
        deal={null}
        open={isDealDialogOpen}
        onClose={() => setIsDealDialogOpen(false)}
        defaultContactId={id}
      />}

      {isTaskDialogOpen && <TaskDialog
        task={null}
        open={isTaskDialogOpen}
        onClose={() => setIsTaskDialogOpen(false)}
        defaultContactId={id}
      />}

      {isProjectDialogOpen && <ProjectDialog
        project={null}
        open={isProjectDialogOpen}
        onClose={() => setIsProjectDialogOpen(false)}
        defaultContactId={id}
      />}

      {isEmployeeDialogOpen && <ContactDialog
        contact={null}
        open={isEmployeeDialogOpen}
        onClose={() => setIsEmployeeDialogOpen(false)}
        defaultCompanyId={id}
      />}

      {isEditDialogOpen && <ContactDialog
        contact={contact}
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
      />}

      <DeleteConfirmation
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={() => { deleteContact.mutateAsync({ id, name: contact.name }); router.push("/dashboard/contacts"); }}
        entityType="contact"
        entityName={contact.name}
      />
    </div>
  );
});


import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/contact')({
  component: ContactDetailPage,
  pendingComponent: () => <DetailPageSkeleton />,
  pendingMs: 0,
  pendingMinMs: 150,
});
